import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useSiteSettings } from "@/lib/cms";
import type { ContactFormField } from "@shared/schema";

/** Phrase inside a checkbox label that becomes a privacy-policy link. */
const PRIVACY_LABEL_PHRASE = "adatvédelmi tájékoztató";

/**
 * Renders a checkbox label, turning the "adatvédelmi tájékoztató" phrase into
 * a link when a privacy-policy URL is configured. Falls back to plain text if
 * no URL is set or the phrase isn't present in the label.
 */
function renderCheckboxLabel(label: string, url: string | null) {
  if (!url) return label;
  const idx = label.toLowerCase().indexOf(PRIVACY_LABEL_PHRASE);
  if (idx === -1) return label;
  const before = label.slice(0, idx);
  const match = label.slice(idx, idx + PRIVACY_LABEL_PHRASE.length);
  const after = label.slice(idx + PRIVACY_LABEL_PHRASE.length);
  return (
    <>
      {before}
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-700 underline hover:text-brand-900"
        onClick={(e) => e.stopPropagation()}
      >
        {match}
      </a>
      {after}
    </>
  );
}

type SubmitArgs = {
  payload: Record<string, unknown>;
  projectId?: string;
  unitId?: string;
};

type DynamicFormProps = {
  projectId?: string;
  unitId?: string;
  unitLabel?: string;
  compact?: boolean;
};

export function DynamicForm({
  projectId,
  unitId,
  unitLabel,
  compact,
}: DynamicFormProps) {
  const { t } = useTranslation();
  const [values, setValues] = useState<Record<string, unknown>>({});
  const [success, setSuccess] = useState(false);

  const fieldsQuery = useQuery({
    queryKey: ["contact-fields"],
    queryFn: () =>
      api<ContactFormField[]>("/api/contact/fields", { withAuth: false }),
  });

  const submit = useMutation({
    mutationFn: (args: SubmitArgs) =>
      api<{ id: string }>("/api/contact", {
        method: "POST",
        withAuth: false,
        body: {
          payload: args.payload,
          projectId: args.projectId,
          unitId: args.unitId,
          sourceUrl:
            typeof window !== "undefined" ? window.location.href : undefined,
        },
      }),
    onSuccess: () => {
      setSuccess(true);
      setValues({});
    },
  });

  if (fieldsQuery.isLoading) {
    return (
      <div className="text-sm text-brand-500 py-4">{t("common.loading")}</div>
    );
  }

  if (fieldsQuery.isError || !fieldsQuery.data) {
    return (
      <div className="text-sm text-red-600 py-4">{t("common.error")}</div>
    );
  }

  const fields = fieldsQuery.data;

  function handleChange(key: string, value: unknown) {
    setValues((v) => ({ ...v, [key]: value }));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSuccess(false);
    submit.mutate({ payload: values, projectId, unitId });
  }

  if (success) {
    return (
      <div className="rounded-md bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800">
        {t("contact.form.success")}
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className={compact ? "space-y-3" : "space-y-4"}
    >
      {unitLabel && (
        <div className="rounded-md bg-brand-50 border border-brand-200 px-3 py-2 text-xs text-brand-600">
          Érdeklődés: <span className="font-medium">{unitLabel}</span>
        </div>
      )}
      {fields.map((f) => (
        <FieldInput
          key={f.id}
          field={f}
          value={values[f.key]}
          onChange={(v) => handleChange(f.key, v)}
          compact={compact}
        />
      ))}
      {submit.isError && (
        <div className="text-sm text-red-600">{t("contact.form.error")}</div>
      )}
      <button
        type="submit"
        className="btn-primary"
        disabled={submit.isPending}
      >
        {submit.isPending ? t("contact.form.sending") : t("contact.form.submit")}
      </button>
    </form>
  );
}

function FieldInput({
  field,
  value,
  onChange,
  compact,
}: {
  field: ContactFormField;
  value: unknown;
  onChange: (v: unknown) => void;
  compact?: boolean;
}) {
  const id = `f-${field.id}`;
  const siteSettings = useSiteSettings();
  const privacyPolicyUrl = siteSettings.contact.privacyPolicyUrl;
  const labelEl = (
    <label htmlFor={id} className={compact ? "label text-xs" : "label"}>
      {field.label}
      {field.required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
  );

  if (field.type === "textarea") {
    return (
      <div>
        {labelEl}
        <textarea
          id={id}
          required={field.required}
          rows={compact ? 3 : 4}
          placeholder={field.placeholder ?? undefined}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="input resize-y"
        />
        {field.helpText && (
          <p className="mt-1 text-xs text-brand-500">{field.helpText}</p>
        )}
      </div>
    );
  }

  if (field.type === "select") {
    return (
      <div>
        {labelEl}
        <select
          id={id}
          required={field.required}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(e.target.value)}
          className="input"
        >
          <option value="">— válasszon —</option>
          {field.options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        {field.helpText && (
          <p className="mt-1 text-xs text-brand-500">{field.helpText}</p>
        )}
      </div>
    );
  }

  if (field.type === "checkbox") {
    return (
      <div>
        <label
          htmlFor={id}
          className="flex items-start gap-2 text-sm text-brand-700 cursor-pointer"
        >
          <input
            id={id}
            type="checkbox"
            required={field.required}
            checked={value === true}
            onChange={(e) => onChange(e.target.checked)}
            className="mt-0.5"
          />
          <span>
            {renderCheckboxLabel(field.label, privacyPolicyUrl)}
            {field.required && <span className="text-red-500 ml-0.5">*</span>}
          </span>
        </label>
        {field.helpText && (
          <p className="mt-1 text-xs text-brand-500">{field.helpText}</p>
        )}
      </div>
    );
  }

  return (
    <div>
      {labelEl}
      <input
        id={id}
        type={field.type === "tel" ? "tel" : field.type === "email" ? "email" : "text"}
        required={field.required}
        placeholder={field.placeholder ?? undefined}
        value={typeof value === "string" ? value : ""}
        onChange={(e) => onChange(e.target.value)}
        className="input"
      />
      {field.helpText && (
        <p className="mt-1 text-xs text-brand-500">{field.helpText}</p>
      )}
    </div>
  );
}
